// AnnonceCard.js
import React, { useState, useEffect } from 'react';
import './AnnonceCard.css';
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { getFinancingEligibility } from './financingEligibility';

function AnnonceCard({ vehicle, onVehicleClick }) {
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);

  const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');
  const isSpiderSource = cleanText(vehicle?.source).toLowerCase() === 'spidervo';
  const displayTitle = [cleanText(vehicle?.marque), cleanText(vehicle?.modele)].filter(Boolean).join(' ');
  const vehicleReference = vehicle?.reference || vehicle?.id;
  const vehicleUrl = vehicleReference ? `/vehicules/${encodeURIComponent(vehicleReference)}` : "/nos-vehicules";
  const spiderDisplayVersion = [
    cleanText(vehicle?.spiderVersion),
    cleanText(vehicle?.version),
  ].find(Boolean) || '';
  const fallbackDisplayVersion = [
    cleanText(vehicle?.modelePrecis),
    cleanText(vehicle?.modeleprecis),
    cleanText(vehicle?.modele_precis),
    cleanText(vehicle?.["Modèle précis"]),
    cleanText(vehicle?.finition),
    cleanText(vehicle?.Finition),
  ].find(Boolean) || '';
  const displayVersion = isSpiderSource ? spiderDisplayVersion : fallbackDisplayVersion;

  // Transforme /images/REF/photoN.jpg → /img/REF/photoN.jpg?w=648 pour servir du WebP redimensionné
  const toThumb = (url) => {
    const thumbnailUrl = url.replace('/images/', '/img/');
    return `${thumbnailUrl}${thumbnailUrl.includes('?') ? '&' : '?'}w=648`;
  };

  useEffect(() => {
    setImageLoaded(false);

    // La liste ne charge que la photo de couverture. La galerie complète reste dans la fiche.
    if (vehicle.source === 'spidervo' && Array.isArray(vehicle.images) && vehicle.images.length > 0) {
      const coverImage = vehicle.images.find(url => typeof url === 'string' && url.trim() && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith(process.env.PUBLIC_URL)));
      setImageUrl(coverImage ? toThumb(coverImage) : process.env.PUBLIC_URL + '/vdispo.png');
      return;
    }

    // Pour les anciens véhicules Firebase, ne récupère que la première photo.
    async function fetchImages() {
      if (!vehicle.id) {
        setImageUrl(process.env.PUBLIC_URL + '/vdispo.png');
        return;
      }
      const storage = getStorage();
      const imageFolderRef = ref(storage, `VOITURES/${vehicle.id}`);
      try {
        const images = await listAll(imageFolderRef);
        const firstImage = images.items[0];
        setImageUrl(firstImage ? await getDownloadURL(firstImage) : process.env.PUBLIC_URL + '/vdispo.png');
      } catch (error) {
        setImageUrl(process.env.PUBLIC_URL + '/vdispo.png');
      }
    }
    fetchImages();
  }, [vehicle]);

  if (!vehicle) {
    // Handle the case where the vehicle is not defined
    return null; // or some default content
  }

  // Garantie : nombre de mois du CRM, 12 par défaut
  const garantieMois = vehicle.garantieMois > 0 ? vehicle.garantieMois : 12;
  const garantieLabel = `${garantieMois} mois`;

  const getFaibleKmText = () => {
    const km = Number(vehicle.kilometrage);
    const dateCirc = vehicle.dateCirculation ? new Date(vehicle.dateCirculation) : null;
    if (!dateCirc || !km || km >= 100000) return null;
    const ageAnnees = (new Date() - dateCirc) / (1000 * 60 * 60 * 24 * 365.25);
    if (ageAnnees < 0.5) return null;
    const kmParAn = km / ageAnnees;
    if (kmParAn <= 6500) {
      return (
        <div className="faible-km">
          <img className='imgfaiblekm' src='faiblekm.svg' alt='picto_faible_km'/>
          <p className='pfaiblekm'>Faible kilométrage</p>
        </div>
      );
    }
    return null;
  }

  const date = vehicle.dateCirculation ? new Date(vehicle.dateCirculation) : new Date();
  const createdAt = vehicle.createdAt ? new Date(vehicle.createdAt) : null;
  const isNewVehicle = Boolean(
    createdAt &&
      !Number.isNaN(createdAt.getTime()) &&
      Date.now() - createdAt.getTime() >= 0 &&
      Date.now() - createdAt.getTime() < 11 * 24 * 60 * 60 * 1000
  );
  const financing = getFinancingEligibility(vehicle);

  var month = date.getMonth();
  const year = date.getFullYear();
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];
  month = monthNames[month];

  if (vehicle.vendu === true) {
    return (
      <div className="annonce-card-vendu">
        <div className="slider-shell">
          {isNewVehicle && <span className="card-new-badge">Nouveau</span>}
          <div className={`slider-image-wrap ${imageLoaded ? "is-loaded" : ""}`}>
            <img
              className="slider-image"
              src={imageUrl || process.env.PUBLIC_URL + '/vdispo.png'}
              alt={displayTitle || 'Véhicule disponible'}
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
            />
          </div>
        </div>
        <div className="vehicle-info">
        <h3 className="vehicle-title">{displayTitle || 'Véhicule disponible'}</h3>
          <p className="vehicle-detailsPrecis">{displayVersion}</p>
          <p className="vehicle-details ">{`${month} ${year} | ${vehicle.kilometrage.toLocaleString()} km | ${vehicle.carburant} | ${vehicle.boiteVitesse} `}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="annonce-card" onClick={onVehicleClick}>
      <div className="slider-shell">
        {isNewVehicle && <span className="card-new-badge">Nouveau</span>}
        {financing.credit && (
          <div className="card-financing-badges">
            <span className="card-financing-badge">Financement</span>
          </div>
        )}
        <div className={`slider-image-wrap ${imageLoaded ? "is-loaded" : ""}`}>
          <img
            className="slider-image"
            src={imageUrl || process.env.PUBLIC_URL + '/vdispo.png'}
            alt={displayTitle || 'Véhicule disponible'}
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
          />
        </div>
      </div>
      <div className="vehicle-info">
        <div className="vehicle-header">
          <h3 className="vehicle-title">
            <a
              className="vehicle-title-link"
              href={vehicleUrl}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onVehicleClick();
              }}
            >
              {displayTitle || 'Véhicule disponible'}
            </a>
          </h3>
          <p className="vehicle-detailsPrecis vehicle-modele-precis">{displayVersion}</p>
        </div>

        <hr className="bar-separation"/>

        <div className="vehicle-specs-grid">
          <div className="vehicle-spec-item" title="Date de mise en circulation">
            <img src="/calendar.svg" alt="Date" className="vehicle-spec-icon" />
            <span>{month} {year}</span>
          </div>
          <div className="vehicle-spec-item" title="Kilométrage">
            <img src="/kilometre.svg" alt="Kilométrage" className="vehicle-spec-icon" />
            <span>{Number(vehicle.kilometrage).toLocaleString('fr-FR')} km</span>
          </div>
          <div className="vehicle-spec-item" title="Carburant">
            <img src="/carburant.svg" alt="Carburant" className="vehicle-spec-icon" />
            <span>{vehicle.carburant}</span>
          </div>
          <div className="vehicle-spec-item" title="Boîte de vitesse">
            <img src="/boite.svg" alt="Boîte de vitesse" className="vehicle-spec-icon" />
            <span>{vehicle.boiteVitesse}</span>
          </div>
        </div>

        <div className="vehicle-price spaced">
          <span>{`${Number(vehicle.prix).toLocaleString('fr-FR')} €`}</span>
          <span className="vehicle-price-tax">TVA incluse</span>
        </div>

        <div className="card-points-forts">
          {[
            (
              <div className="card-point-fort-item" key="g">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11.5 14.5 16 10"/></svg>
                <div className="card-point-fort-text">
                  <span className="card-point-fort-label">Garantie</span>
                  <span className="card-point-fort-value">{garantieLabel}</span>
                </div>
              </div>
            ),
            Number(vehicle.nbrmain) === 1 && (
              <div className="card-point-fort-item" key="pm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><path d="M17 11l2 2 4-4"/></svg>
                <div className="card-point-fort-text">
                  <span className="card-point-fort-label">Propriétaire</span>
                  <span className="card-point-fort-value">1ère main</span>
                </div>
              </div>
            ),
            Number(vehicle.nbrmain) === 2 && vehicle.dateCirculation && ((new Date() - new Date(vehicle.dateCirculation)) / (1000 * 60 * 60 * 24 * 365.25)) > 6 && (
              <div className="card-point-fort-item" key="pm2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><path d="M17 11l2 2 4-4"/></svg>
                <div className="card-point-fort-text">
                  <span className="card-point-fort-label">Propriétaire</span>
                  <span className="card-point-fort-value">2ème main</span>
                </div>
              </div>
            ),
            (!vehicle.vehiculeimporte || vehicle.vehiculeimporte === 'non') && (
              <div className="card-point-fort-item" key="fr">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                <div className="card-point-fort-text">
                  <span className="card-point-fort-label">Origine</span>
                  <span className="card-point-fort-value">France</span>
                </div>
              </div>
            ),
            getFaibleKmText() && (
              <div className="card-point-fort-item" key="km">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <div className="card-point-fort-text">
                  <span className="card-point-fort-label">Kilométrage</span>
                  <span className="card-point-fort-value">Faible</span>
                </div>
              </div>
            ),
          ].filter(Boolean).slice(0, 3)}
        </div>

        <div className="vehicle-localisation vehicle-location-row">
          <img src="/location-black.svg" alt="Localisation" className="vehicle-location-icon" />
          <span>{vehicle.localisation} (78)</span>
          {vehicle.livraison && (
            <span className="card-livraison-tag">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              Livraison possible
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnnonceCard;
